import React, { useContext, useMemo } from "react";
import classnames from "classnames";
import { useDetailAwareParams, useServerAwareState } from "../CGrid/ServerGrid";
import { useSpace } from "../../Contexts/SpaceContext";
import { buildODataFilter } from "../../Helpers/ODataHelper";
import moment from "moment";
import { useAgendaPreferences } from "../AgendaPreferences/AgendaPreferences";
import { spaceTasksSchema } from "../../config/schema";
import dayjs from "dayjs";

export const CalenderRefetchRefContext = React.createContext(null);
export const useReftchRefProvider = () => useContext(CalenderRefetchRefContext);
export enum CalendarViewEnum {
  Month = 1,
  Week = 2,
  Day = 3,
  WorkWeek = 4,
  MultipleDay = 5,
}
export const isValidCalendarView = (view: number) => {
  switch (view) {
    case CalendarViewEnum.Month:
    case CalendarViewEnum.Week:
    case CalendarViewEnum.WorkWeek:
    case CalendarViewEnum.MultipleDay:
    case CalendarViewEnum.Day:
      return true;

    default:
      return false;
  }
};

export const weekDayTranslation = {
  0: "SUNDAY",
  1: "MONDAY",
  2: "TUESDAY",
  3: "WEDNESDAY",
  4: "THURSDAY",
  5: "FRIDAY",
  6: "SATURDAY",
};

export const monthTranslation = {
  0: "JANUARY",
  1: "FEBRUARY",
  2: "MARCH",
  3: "APRIL",
  4: "MAY",
  5: "JUNE",
  6: "JULY",
  7: "AUGUST",
  8: "SEPTEMBER",
  9: "OCTOBER",
  10: "NOVEMBER",
  11: "DECEMBER",
};

export const CalendarUpdateDateContext = React.createContext<any>(undefined)


export const calendarToEpochDays = (date: Date) => {
  const total_seconds = date.getTime() / 1000;
  const total_minutes = total_seconds / 60;
  const total_hours = total_minutes / 60;
  const days = Math.ceil(total_hours / 24);
  return days;
};

export const CalendarItemContext = React.createContext<any>(undefined);

export const CalendarItem = ({
  children,
  onClick: overrideOnClick,
  onDoubleClick,
  className: optionalClassnames,
}: any) => {
  const { anchorRef, onClick, className, onMouseOver, onMouseOut } =
    useContext(CalendarItemContext);

  return (
    <div
      ref={anchorRef}
      onMouseOver={onMouseOver}
      onMouseOut={onMouseOut}
      onClick={overrideOnClick || onClick}
      onDoubleClick={onDoubleClick}
      className={classnames(className, optionalClassnames)}
    >
      {children}
    </div>
  );
};

const formatDate = (date: Date) => {
  return `datetime'${moment(date).format("YYYY-MM-DD")}'`;
};

const top = 100000;

export const useCalendarDayDateUrl = (dateInfo: any, settings: any) => {
  const viewAwareStuff = useServerAwareState();
  const { params, moduleType } = viewAwareStuff || {};

  const viewDetailedAwareStuff = useDetailAwareParams(params, moduleType);
  const { filter, query } = viewDetailedAwareStuff || {};

  const { advancedFilters } = params || {};
  const space = useSpace();

  const selectedUser = useContext(AgendaSelectedUserContext);

  const agendaPreferences = useAgendaPreferences();
  const url = useMemo(() => {
    const { BeginDateField, EndDateField, schema, extraInbuiltFilter, isAgendaTask } =
      settings;
    let baseUrl = schema?.getCalendarEndpoint
      ? schema.getCalendarEndpoint(space.Id, selectedUser)
      : schema.getEndpoint(space.Id);

    let resFilter = Array.isArray(extraInbuiltFilter)
      ? buildODataFilter([...filter, ...extraInbuiltFilter], false, true)
      : buildODataFilter(filter, false, true);
    resFilter = resFilter.substring(9, resFilter.length - 1);

    const { startDate, endDate } = dateInfo;
    const resolvedStartDate = new Date(startDate);
    resolvedStartDate.setDate(resolvedStartDate.getDate());

    const resolvedEndDate = new Date(endDate);
    resolvedEndDate.setDate(resolvedEndDate.getDate());

    if (isAgendaTask) {
      const startStringT = moment(resolvedStartDate).format("YYYY-MM-DD");
      const endStringT = moment(resolvedEndDate).format("YYYY-MM-DD");
      const filters = [
        {
          operator: "le",
          field: "Task/BeginDateNoHours",
          value: `datetime'${endStringT}'`,
          type: "date",
          data: {
            value: `datetime'${endStringT}'`,
            type: "date",
          },
        },
        {
          operator: "ge",
          field: "Task/EndDateNoHours",
          value: `datetime'${startStringT}'`,
          type: "date",
          data: {
            value: `datetime'${startStringT}'`,
            type: "date",
          },
        },
        {
          operator: "eq",
          field: "Assigned/Id",
          value: selectedUser,
          data: {
            type: "string",
          },
        },
      ];
      const resolvedFilter = buildODataFilter(filters, false, true);

      return `${baseUrl}?${resolvedFilter}&$inlinecount=allpages&$top=10000`;
    }

    if (schema.name === "SpaceAgenda" || schema.name === "SpaceOutlookEvents") {
      const startString = moment(resolvedStartDate).format("YYYY-MM-DD");
      const endString = moment(resolvedEndDate).format("YYYY-MM-DD");
      let r = `${baseUrl}?begin=${startString}&end=${endString}`;

      if (agendaPreferences && agendaPreferences.all) {
        r += "&all=true";
      }
      return r;
    }

    const calendarFilter = `(((${BeginDateField} gt ${formatDate(
      resolvedStartDate,
    )}) and (${BeginDateField} lt ${formatDate(
      resolvedEndDate,
    )})) or ((${BeginDateField} lt ${formatDate(
      resolvedStartDate,
    )}) and (${EndDateField} gt ${formatDate(resolvedStartDate)})))`;

    resFilter = resFilter
      ? `${resFilter} and ${calendarFilter}`
      : calendarFilter;

    baseUrl += `?$inlinecount=allpages&$filter=(${resFilter})&$top=${top}${
      query ? `&query=${query}` : ""
    }`;

    if (advancedFilters && advancedFilters.length > 0)
      baseUrl += `&customFilters=${advancedFilters.join(",")}`;

    return baseUrl;
  }, [
    advancedFilters,
    agendaPreferences,
    dateInfo,
    filter,
    query,
    selectedUser,
    settings,
    space.Id,
  ]);

  return url;
};

export const AgendaSelectedUserContext = React.createContext(null);

export const useAgendaDayDateUrl = (dateInfo: any, settings: any) => {
  const selectedUser = useContext(AgendaSelectedUserContext);
  const agendaPreferences = useAgendaPreferences();
  const space = useSpace();
  const { schema } = settings;
  let baseUrl = schema?.getCalendarEndpoint
    ? schema.getCalendarEndpoint(space.Id, selectedUser)
    : schema.getEndpoint(space.Id);

  const { startDate, endDate } = dateInfo;
  const resolvedStartDate = new Date(startDate);
  resolvedStartDate.setDate(resolvedStartDate.getDate());

  const resolvedEndDate = new Date(endDate);
  resolvedEndDate.setDate(resolvedEndDate.getDate());

  const startString = moment(resolvedStartDate).format("YYYY-MM-DD");
  const endString = moment(resolvedEndDate).format("YYYY-MM-DD");

  let r = `${baseUrl}?begin=${startString}&end=${endString}`;

  if (agendaPreferences && agendaPreferences.all) {
    r += "&all=true";
  }
  return r;
};
export const useAgendaNotesDateUrl = (dateInfo: any, settings: any) => {
  const selectedUser = useContext(AgendaSelectedUserContext);
  const agendaPreferences = useAgendaPreferences();
  const space = useSpace();
  const { schema } = settings;
  let baseUrl = schema?.getCalendarEndpoint
    ? schema.getCalendarEndpoint(space.Id, selectedUser)
    : schema.getEndpoint(space.Id);

  const { startDate, endDate } = dateInfo;
  const resolvedStartDate = new Date(startDate);
  resolvedStartDate.setDate(resolvedStartDate.getDate() - 1);

  const resolvedEndDate = new Date(endDate);
  resolvedEndDate.setDate(resolvedEndDate.getDate() + 1);

  const startString = moment(resolvedStartDate).format("YYYY-MM-DD");
  const endString = moment(resolvedEndDate).format("YYYY-MM-DD");

  let r = `${baseUrl}?begin=${startString}&end=${endString}`;
  if (agendaPreferences && agendaPreferences.all) {
    r += "&all=true";
  }

  return r;
};
export const useOutlookEventsDateUrl = (dateInfo: any, settings: any) => {
  const selectedUser = useContext(AgendaSelectedUserContext);
  const space = useSpace();
  const { schema } = settings;
  let baseUrl = schema?.getCalendarEndpoint
    ? schema.getCalendarEndpoint(space.Id, selectedUser)
    : schema.getEndpoint(space.Id);

  const { startDate, endDate } = dateInfo;
  const resolvedStartDate = new Date(startDate);
  resolvedStartDate.setDate(resolvedStartDate.getDate());

  const resolvedEndDate = new Date(endDate);
  resolvedEndDate.setDate(resolvedEndDate.getDate());

  const startString = moment(resolvedStartDate).format("YYYY-MM-DD");
  const endString = moment(resolvedEndDate).format("YYYY-MM-DD");

  let r = `${baseUrl}?begin=${startString}&end=${endString}`;

  return r;
};

export const useTarefasDateUrl = (dateInfo: any) => {
  const selectedUser = useContext(AgendaSelectedUserContext);
  const space = useSpace();
  let baseUrl = spaceTasksSchema.getCalendarEndpoint(space.Id, selectedUser);
  const { startDate, endDate } = dateInfo;

  const startString = moment(startDate).format("YYYY-MM-DD");
  const endString = moment(endDate).format("YYYY-MM-DD");

  const filters = [
    {
      operator: "le",
      field: "Task/BeginDateNoHours",
      value: `datetime'${endString}'`,
      type: "date",
      data: {
        value: `datetime'${endString}'`,
        type: "date",
      },
    },
    {
      operator: "ge",
      field: "Task/EndDateNoHours",
      value: `datetime'${startString}'`,
      type: "date",
      data: {
        value: `datetime'${startString}'`,
        type: "date",
      },
    },
    {
      operator: "eq",
      field: "Assigned/Id",
      value: selectedUser,
      data: {
        type: "string",
      },
    },
  ];
  const resolvedFilter = buildODataFilter(filters, false, true);

  return `${baseUrl}?${resolvedFilter}`;
};

export const outlookMutateBeforeNormalization = (entity: any) => {
  const newEntity = { ...entity };
  const { results } = newEntity;
  const freakinExtraResults: any[] = [];

  const newResults = Array.isArray(results)
    ? results.map((r) => {
        const newR = { ...r };
        const { Id, Subject, JoinUrl, Start, End, Preview, NumberOfDays } =
          newR;
        newR._isOutlookEvent = true;
        newR.CanEdit = false;
        newR.CanDuplicate = false;
        newR.CanDelete = false;
        newR.WasDeleted = true;

        newR.MeetingUrl = JoinUrl;
        newR.Preview = Preview?.replace(/_|/g, "");
        newR.Description = Subject + ` ${newR.Preview} `;

        if (newR.IsAllDay) {
          newR.BeginDate = new Date(`${Start.Date.split("T")[0]}T00:00:00`);
          let ed = dayjs(new Date(`${End.Date.split("T")[0]}T23:59:00`));
          const resolvedEnd = ed.subtract(1, "day");
          newR.EndDate = resolvedEnd.toDate();
        } else {
          newR.BeginDate = new Date(`${Start.Date}Z`);
          newR.EndDate = new Date(`${End.Date}Z`);
        }
        newR.Duration = newR.EndDate.getTime() - newR.BeginDate.getTime();
        return newR;
      })
    : [];

  newEntity.results = [...newResults, ...freakinExtraResults];

  return newEntity;
};

export const useCalendarDateUrl = (dateInfo: any, settings: any) => {
  const viewAwareStuff = useServerAwareState();

  const agendaPreferences = useAgendaPreferences();
  const { params, moduleType } = viewAwareStuff || {};
  const viewDetailedAwareStuff = useDetailAwareParams(params, moduleType);
  const { filter, query } = viewDetailedAwareStuff || {};

  const { advancedFilters } = params || {};
  const space = useSpace();

  const selectedUser = useContext(AgendaSelectedUserContext);

  const url = useMemo(() => {
    const {
      BeginDateField,
      EndDateField,
      schema,
      isAgendaTask,
      extraInbuiltFilter,
      addCreatedByFilter,
    } = settings;

    let baseUrl = schema?.getCalendarEndpoint
      ? schema.getCalendarEndpoint(space.Id, selectedUser)
      : schema.getEndpoint(space.Id);
    let resFilter = Array.isArray(extraInbuiltFilter)
      ? buildODataFilter([...filter, ...extraInbuiltFilter], false, true)
      : addCreatedByFilter
        ? buildODataFilter(
            [
              ...filter,
              {
                operator: "eq",
                field: "Account/Id",
                value: selectedUser,
                data: {
                  type: "string",
                },
              },
            ],
            false,
            true,
          )
        : buildODataFilter(filter, false, true);
    resFilter = resFilter.substring(9, resFilter.length - 1);

    const { startDate, endDate } = dateInfo;
    const resolvedStartDate = new Date(startDate);
    resolvedStartDate.setDate(
      schema.name === "SpaceOutlookEvents"
        ? resolvedStartDate.getDate()
        : resolvedStartDate.getDate(),
    );

    const resolvedEndDate = new Date(endDate);
    resolvedEndDate.setDate(
      schema.name === "SpaceOutlookEvents"
        ? resolvedEndDate.getDate()
        : resolvedEndDate.getDate() + 1,
    );

    if (isAgendaTask) {
      const startStringT = moment(resolvedStartDate).format("YYYY-MM-DD");
      const endStringT = moment(resolvedEndDate).format("YYYY-MM-DD");
      const filters = [
        {
          operator: "le",
          field: "Task/BeginDateNoHours",
          value: `datetime'${endStringT}'`,
          type: "date",
          data: {
            value: `datetime'${endStringT}'`,
            type: "date",
          },
        },
        {
          operator: "ge",
          field: "Task/EndDateNoHours",
          value: `datetime'${startStringT}'`,
          type: "date",
          data: {
            value: `datetime'${startStringT}'`,
            type: "date",
          },
        },
        {
          operator: "eq",
          field: "Assigned/Id",
          value: selectedUser,
          data: {
            type: "string",
          },
        },
      ];
      const resolvedFilter = buildODataFilter(filters, false, true);

      return `${baseUrl}?${resolvedFilter}&$inlinecount=allpages&$top=10000`;
    }

    if (schema.name === "SpaceAgenda" || schema.name === "SpaceOutlookEvents") {
      const startString = moment(resolvedStartDate).format("YYYY-MM-DD");
      const endString = moment(resolvedEndDate).format("YYYY-MM-DD");
      let r = `${baseUrl}?begin=${startString}&end=${endString}`;

      if (agendaPreferences && agendaPreferences.all) {
        r += "&all=true";
      }

      return r;
    }

    const calendarFilter = `(((${BeginDateField} ge ${formatDate(
      resolvedStartDate,
    )}) and (${BeginDateField} le ${formatDate(
      resolvedEndDate,
    )})) or ((${BeginDateField} le ${formatDate(
      resolvedStartDate,
    )}) and (${EndDateField} ge ${formatDate(resolvedStartDate)})))`;

    resFilter = resFilter
      ? `${resFilter} and ${calendarFilter}`
      : calendarFilter;

    baseUrl += `?$inlinecount=allpages&$filter=(${resFilter})&$top=${top}${
      query ? `&query=${query}` : ""
    }`;

    if (advancedFilters && advancedFilters.length > 0)
      baseUrl += `&customFilters=${advancedFilters.join(",")}`;

    return baseUrl;
  }, [
    advancedFilters,
    agendaPreferences,
    dateInfo,
    filter,
    query,
    selectedUser,
    settings,
    space.Id,
  ]);

  return url;
};
